import { useState } from "react";
import { Badge, Button, Form, Offcanvas } from "react-bootstrap";
import { FaPencilAlt } from "react-icons/fa";
import { updateClaim } from "../../services/apiClient.js";
import { claimMailtoHref, claimMailtoTitle } from "../../utils/claimEmail.js";
import Hint from "./../shared/Hint.jsx";

// "Job at a glance" — the WebForms header's info, one click away instead of
// permanent chrome. Lives next to the claim identity in the workspace sidebar so
// any tab can peek at the key claim facts (insurer, assessor, PM, address, …)
// without leaving the tab or giving up vertical space.
//
// QUICK FACTS (RDR Note · Lockbox · Job Summary) are EDITABLE here — the 2.0
// answer to 1.0's Progress-page quick box. Their home is the Details tab; this
// is the same field through the one sidebar every tab already has, one save
// path (PUT /claims/:id) — access from anywhere, never a second home
// (no-duplicate-entry rule, Robert 2026-07-16).

// The assessor-routing contacts, in 1.0's header order.
const ROUTING_ROLES = [
  ["assessor_updates_to", "Updates To"],
  ["assessor_quotes_to", "Quotes To"],
  ["assessor_invoices_to", "Invoices To"],
  ["assessor_variations_to", "Variations To"],
];

function Row({ label, value }) {
  return (
    <div className="d-flex justify-content-between gap-3 py-1 border-bottom">
      <span className="text-muted small text-nowrap">{label}</span>
      <span className="small text-end" style={{ whiteSpace: "pre-wrap" }}>{value || "—"}</span>
    </div>
  );
}

// Email rows use the shared claim-email rules (src/utils/claimEmail.js): 1.0's
// subject line, and the contact's own CC list carried through — which is what
// makes Invoices To copy Accounts and Support.
function EmailRow({ label, text, email, cc, claim, client = false }) {
  // `client` = the homeowner, who gets 1.0's client subject line rather than the
  // reference-laden one the trade gets (utils/claimEmail.js).
  const href = claimMailtoHref(email, claim, cc, { client });
  return (
    <div className="d-flex justify-content-between gap-3 py-1 border-bottom">
      <span className="text-muted small text-nowrap">{label}</span>
      <span className="small text-end" style={{ whiteSpace: "pre-wrap" }}>
        {href ? (
          <a href={href} data-hint={claimMailtoTitle(email, cc)}>
            {text || email}
          </a>
        ) : (
          text || "—"
        )}
      </span>
    </div>
  );
}

// One editable quick fact: click the value (or pencil) → input → Enter/Save.
function QuickFact({ claim, field, label, multiline = false, value, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  function start() {
    setDraft(value || "");
    setError("");
    setEditing(true);
  }
  async function save() {
    setBusy(true);
    setError("");
    try {
      await updateClaim(claim.id, { [field]: draft.trim() || null });
      onSaved(field, draft.trim() || null);
      setEditing(false);
    } catch (err) {
      setError(err.response?.data?.error || "Unable to save.");
    } finally {
      setBusy(false);
    }
  }

  if (editing) {
    return (
      <div className="py-1 border-bottom">
        <div className="text-muted small">{label}</div>
        <Form.Control
          size="sm"
          as={multiline ? "textarea" : "input"}
          rows={multiline ? 3 : undefined}
          value={draft}
          autoFocus
          disabled={busy}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !multiline) save();
            if (e.key === "Escape") setEditing(false);
          }}
        />
        {error ? <div className="small text-danger">{error}</div> : null}
        <div className="d-flex gap-1 mt-1">
          <Button size="sm" onClick={save} disabled={busy}>Save</Button>
          <Button size="sm" variant="secondary" onClick={() => setEditing(false)} disabled={busy}>
            Cancel
          </Button>
        </div>
      </div>
    );
  }
  return (
    <div
      role="button"
      className="d-flex justify-content-between gap-3 py-1 border-bottom glance-quick-fact"
      data-hint={`Click to edit ${label.toLowerCase()}`}
      onClick={start}
    >
      <span className="text-muted small text-nowrap">
        {label} <FaPencilAlt className="opacity-50" style={{ fontSize: "0.65rem" }} />
      </span>
      <span className="small text-end" style={{ whiteSpace: "pre-wrap" }}>
        {value || <span className="text-muted">—</span>}
      </span>
    </div>
  );
}

// One quick FLAG (Important / Problem / Contractor Quoting — 1.0's Progress-page
// checkboxes, homed on Details Flags): a switch that saves immediately. The
// server rules ride along free (un-ticking Problem Job is Administrator-only;
// ticking it stamps the date) — a refusal shows inline.
function QuickFlag({ claim, field, label, value, onSaved }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function toggle(next) {
    setBusy(true);
    setError("");
    try {
      await updateClaim(claim.id, { [field]: next });
      onSaved(field, next);
    } catch (err) {
      setError(err.response?.data?.error || "Unable to save.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="py-1 border-bottom">
      <Form.Check
        type="switch"
        id={`glance-${field}`}
        label={<span className={`small ${value ? "text-danger fw-semibold" : "text-muted"}`}>{label}</span>}
        checked={!!value}
        disabled={busy}
        onChange={(e) => toggle(e.target.checked)}
      />
      {error ? <div className="small text-danger">{error}</div> : null}
    </div>
  );
}

function ClaimGlance({ claim }) {
  const [show, setShow] = useState(false);
  // Quick-fact saves show immediately without re-fetching the whole summary.
  const [saved, setSaved] = useState({});
  const fact = (field) => (saved[field] !== undefined ? saved[field] : claim?.[field]);
  if (!claim) return null;

  return (
    <>
      {/* Sized to the job-number line it sits on (Robert 2026-08-12): this is one
          of the most-clicked things on the page — it is where the insurer, the
          RDR note and the margins live — and it was a squat little dot beside a
          1.25rem heading. A square box with the glyph centred gives it a real
          target instead of a height derived from padding. */}
      <Hint label="Job at a glance">
        <Button
          variant="outline-secondary"
          size="sm"
          className="rounded-circle d-inline-flex align-items-center justify-content-center p-0"
          style={{ width: "1.55rem", height: "1.55rem", fontSize: "0.9rem", lineHeight: 1 }}
          onClick={(e) => {
            e.currentTarget.blur(); // focus keeps the tooltip alive under/after the panel
            setShow(true);
          }}
          aria-label="Job at a glance"
        >
          i
        </Button>
      </Hint>

      {/* restoreFocus off: refocusing the "i" on close would re-light its tooltip */}
      <Offcanvas show={show} onHide={() => setShow(false)} placement="start" scroll backdrop restoreFocus={false}>
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>
            Claim #{claim.claim_number}{" "}
            <Badge bg="secondary" className="align-middle">{claim.status_name}</Badge>
          </Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          <Row label="Client" value={claim.client_name} />
          <Row label="Contact" value={claim.contact_name} />
          <Row label="Address" value={claim.street_address} />
          <Row label="Mobile" value={claim.mobile_phone} />
          <Row label="Phone" value={claim.business_phone} />
          <EmailRow label="Email" text={claim.email_address} email={claim.email_address} claim={claim} client />
          <Row label="Job type" value={claim.job_type_name} />
          <Row label="Division" value={claim.division_name} />
          <Row label="Insurer" value={claim.insurer_company_name} />
          <Row label="Assessor Co" value={claim.assessor_company_name} />
          <EmailRow label="Assessor" text={claim.assessor_name} email={claim.assessor_email}
            cc={claim.assessor_cc} claim={claim} />
          {/* The assessor's phone, where one is recorded (256 of 328 assessors
              have one) — KS 2026-09-08. A tel: link so a phone or a headset
              dials it; the row is absent rather than "—" when there is none. */}
          {claim.assessor_phone?.trim() ? (
            <div className="d-flex justify-content-between gap-3 py-1 border-bottom">
              <span className="text-muted small text-nowrap">Assessor ph</span>
              <span className="small text-end">
                <a href={`tel:${claim.assessor_phone.replace(/[^\d+]/g, "")}`}>{claim.assessor_phone}</a>
              </span>
            </div>
          ) : null}
          {/* 1.0's header carried all four routing contacts as mailto links
              (ClientInfo.ascx: Updates To · Quote To · Invoice To · Variations
              To). Shown only when they differ from the assessor, who is already
              a row above — they default to the assessor on 97% of jobs. */}
          {ROUTING_ROLES.map(([field, label]) =>
            claim[`${field}_name`] && claim[`${field}_name`] !== claim.assessor_name ? (
              <EmailRow key={field} label={label} text={claim[`${field}_name`]}
                email={claim[`${field}_email`]} cc={claim[`${field}_cc`]} claim={claim} />
            ) : null
          )}
          <Row label="PCO" value={claim.project_coordinator_name} />
          <Row label="Inspector" value={claim.inspector_name} />
          <Row label="Assessor ref" value={claim.assessor_ref} />
          <Row label="Claim number" value={claim.insurance_claim_number} />
          <Row label="Excess" value={claim.property_excess} />

          {/* Editable from any tab — one save path, home stays Details. */}
          <div
            className="text-uppercase fw-semibold small text-muted mt-3 pb-1 border-bottom"
            style={{ letterSpacing: "0.06em" }}
          >
            Quick Facts
          </div>
          <QuickFact claim={claim} field="lockbox_location" label="Lockbox"
            value={fact("lockbox_location")} onSaved={(f, v) => setSaved((s) => ({ ...s, [f]: v }))} />
          <QuickFact claim={claim} field="rdr_note" label="RDR Note" multiline
            value={fact("rdr_note")} onSaved={(f, v) => setSaved((s) => ({ ...s, [f]: v }))} />
          <QuickFact claim={claim} field="job_summary" label="Job Summary" multiline
            value={fact("job_summary")} onSaved={(f, v) => setSaved((s) => ({ ...s, [f]: v }))} />
          {/* 1.0's Progress-page checkboxes — same fields as Details Flags. */}
          <QuickFlag claim={claim} field="is_important_job" label="Important Job"
            value={fact("is_important_job")} onSaved={(f, v) => setSaved((s) => ({ ...s, [f]: v }))} />
          <QuickFlag claim={claim} field="is_problem_job" label="Problem Job"
            value={fact("is_problem_job")} onSaved={(f, v) => setSaved((s) => ({ ...s, [f]: v }))} />
          <QuickFlag claim={claim} field="is_contractor_quoting" label="Contractor Quoting"
            value={fact("is_contractor_quoting")} onSaved={(f, v) => setSaved((s) => ({ ...s, [f]: v }))} />

          <div className="text-muted small mt-2">
            Quick facts save from here; everything else edits on the Details tab.
          </div>
        </Offcanvas.Body>
      </Offcanvas>
    </>
  );
}

export default ClaimGlance;
